import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from "@tanstack/react-router"
import type { IconType } from "react-icons"
import { useTranslation } from "react-i18next"
import {
  FiBookOpen,
  FiBriefcase,
  FiFileText,
  FiLink,
  FiShield,
} from "react-icons/fi"

import { PassportTemplatesService } from "@/client"
import { IconBox, VisionCard } from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-dpp")({
  component: AdminDpp,
})

// `to` is hard-typed against the registered routes so TanStack Router's
// Link can navigate without a runtime string lookup. Listing the tiles
// up here keeps the JSX below small.
type DppTilePath =
  | "/admin-dpp/templates"
  | "/admin-dpp/bindings"
  | "/admin-dpp/records"
  | "/admin-dpp/manufacturers"
  | "/admin-dpp/representatives"

interface DppTile {
  titleKey: string
  descKey: string
  stat: string
  icon: IconType
  to: DppTilePath
}

function AdminDpp() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const { currentOrganization } = useOrganization()
  // `_layout/admin-dpp.templates` etc. are nested children of this route;
  // the child component only mounts when the parent's <Outlet /> is in
  // the tree. We render the overview content only when the URL is the
  // bare `/admin-dpp`, and always render the outlet — TanStack Router
  // renders nothing through it on the overview path.
  const { pathname } = useLocation()
  const isOverview =
    pathname === "/admin-dpp" || pathname === "/admin-dpp/"

  const orgId = currentOrganization?.id

  const templatesQuery = useQuery({
    queryKey: ["passport-templates", orgId],
    queryFn: () =>
      PassportTemplatesService.listPassportTemplates({
        organisationId: orgId!,
        limit: 1,
      }),
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const templateCount = templatesQuery.data?.count ?? 0

  const tiles: DppTile[] = [
    {
      titleKey: "overview.tiles.templates",
      descKey: "overview.tiles.templatesDesc",
      stat: t("overview.tiles.templatesCount", {
        count: templateCount,
        defaultValue: `${templateCount} template${templateCount === 1 ? "" : "s"}`,
      }),
      icon: FiBookOpen,
      to: "/admin-dpp/templates",
    },
    {
      titleKey: "overview.tiles.bindings",
      descKey: "overview.tiles.bindingsDesc",
      stat: "—", // populated in Slice E (bindings browser)
      icon: FiLink,
      to: "/admin-dpp/bindings",
    },
    {
      titleKey: "overview.tiles.records",
      descKey: "overview.tiles.recordsDesc",
      stat: "—",
      icon: FiFileText,
      to: "/admin-dpp/records",
    },
    {
      titleKey: "overview.tiles.manufacturers",
      descKey: "overview.tiles.manufacturersDesc",
      stat: "—",
      icon: FiBriefcase,
      to: "/admin-dpp/manufacturers",
    },
    {
      titleKey: "overview.tiles.representatives",
      descKey: "overview.tiles.representativesDesc",
      stat: "—",
      icon: FiShield,
      to: "/admin-dpp/representatives",
    },
  ]

  return (
    <>
      {isOverview && (
        <Box>
          <Box mb={8}>
            <Heading size="xl" color={sc.heading} mb={2}>
              {t("overview.title")}
            </Heading>
            <Text color={sc.textMuted}>{t("overview.subtitle")}</Text>
          </Box>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={4}
          >
            {tiles.map((tile) => (
              <Link
                key={tile.to}
                to={tile.to}
                style={{ textDecoration: "none" }}
              >
                <VisionCard
                  variant="elevated"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "xl",
                  }}
                >
                  <Flex
                    direction="column"
                    align="center"
                    textAlign="center"
                    py={5}
                    px={4}
                  >
                    <IconBox variant="glass" size="lg" mb={3}>
                      <tile.icon fontSize="24px" color={sc.iconColor} />
                    </IconBox>
                    <Heading size="sm" color={sc.heading} mb={1}>
                      {t(tile.titleKey)}
                    </Heading>
                    <Text color={sc.textMuted} fontSize="xs" mb={2}>
                      {t(tile.descKey)}
                    </Text>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="semibold"
                    >
                      {tile.stat}
                    </Text>
                  </Flex>
                </VisionCard>
              </Link>
            ))}
          </Grid>
        </Box>
      )}
      <Outlet />
    </>
  )
}
